import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { InfiniteLoader } from "react-virtualized";
import { FieldProps, ErrorMessage } from "formik";
import {
  Column,
  Label,
  SelectTable,
  Table,
  Text,
  useFetch,
  useToggle,
  useDebouncedEffect,
} from "@pacerpro/shared-ui";
import { Users } from "@pacerpro/api";
import { filterBy } from "@pacerpro/utils";
import { createAdmin } from "../../reducers/organizations";
import { loadDrawerUsers, loadMoreUserRows } from "../../reducers/drawer";
import { selectDrawerUsers } from "../../selectors";

const filterColumns = ["email"];

const SelectAdmin: React.FunctionComponent<FieldProps> = ({ form }) => {
  const dispatch = useDispatch();
  const { isOpen, onOpen, onClose } = useToggle();
  const [filter, setFilter] = React.useState("");

  const users = useSelector(selectDrawerUsers);

  const [usersResponse, usersResponseLoading] = useFetch(Users.getAll, {
    number: 1,
    size: 50,
  });

  React.useEffect(() => {
    if (!usersResponseLoading && usersResponse) {
      dispatch(loadDrawerUsers(usersResponse));
    }
  }, [usersResponse, usersResponseLoading]);

  useDebouncedEffect(
    () => {
      Users.getAll({
        filter: filterBy(filterColumns, filter),
        number: 1,
        size: 50,
      }).then((response: any) => {
        dispatch(loadDrawerUsers(response.data));
      });
    },
    250,
    [filter],
  );

  const loadMoreRows = () =>
    dispatch(
      loadMoreUserRows({
        filter: filterBy(filterColumns, filter),
        size: 50,
      }),
    );

  return (
    <>
      <Label htmlFor="email" mb={1} align="center">
        Email
      </Label>
      <SelectTable isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
        <SelectTable.Popover>
          <SelectTable.Input
            value={filter}
            onChange={event => setFilter(event.currentTarget.value)}
            onFocus={onOpen}
            buttonProps={{
              onClick: isOpen ? onClose : onOpen,
            }}
          />
          <SelectTable.InputResults
            sx={{
              height: "275px",
              width: "calc(100% + 1px)",
              pr: 2,
            }}
          >
            <InfiniteLoader
              isRowLoaded={({ index }) => !!users[index]}
              // @ts-ignore TODO: (at) refactor dispatch and promise
              loadMoreRows={loadMoreRows}
              rowCount={users.length + 1}
            >
              {({ onRowsRendered, registerChild }) => (
                <Table
                  data={users}
                  onRowsRendered={onRowsRendered}
                  ref={registerChild}
                  onRowClick={async ({ rowData }) => {
                    await dispatch(createAdmin(rowData.id));
                    form.setValues({
                      firstName: rowData.attributes.firstName,
                      lastName: rowData.attributes.lastName,
                      email: rowData.attributes.email,
                      password: rowData.attributes.password || "",
                      office: rowData.attributes.office || "default",
                    });
                    setFilter("");
                    onClose();
                  }}
                  variant="minimal"
                  hasHover={true}
                >
                  <Column
                    dataKey="email"
                    cellDataGetter={({ rowData }) => rowData.attributes.email}
                    width={100}
                    flexGrow={1}
                  />
                </Table>
              )}
            </InfiniteLoader>
          </SelectTable.InputResults>
        </SelectTable.Popover>
      </SelectTable>
      <ErrorMessage component={Text} name="email" sx={{ color: "red" }} />
    </>
  );
};

export default SelectAdmin;
